import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import Loader from '@/components/Loader';
import { MIN_PASSWORD_LENGTH, NewPassword } from '@/components/NewPassword/NewPassword';
import Button from '@/design_system/Button';
import InputText from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Title from '@/design_system/Typography/Title';
import { useCheckSsoQuery } from '@/models/user';
import { useAcceptInvitationAndLogin } from '@/models/userRole';
import { useCurrentSession, useFlags, usePublicKeys } from '@/services/auth';

const Join = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const email = searchParams.get('email')?.replace(/\s/g, '+') ?? '';
  const invitationToken = searchParams.get('token') ?? '';

  const {
    mutateAsync: acceptInvitationAndLogin,
    isPending: isAcceptInvitationAndLoginPending,
    isSuccess: isAcceptInvitationAndLoginSuccess,
  } = useAcceptInvitationAndLogin();

  const { setCurrentSession } = useCurrentSession();
  const { setFlags } = useFlags();
  const { setPublicKeys } = usePublicKeys();

  const { data: { sso: isSso } = {}, isLoading: isCheckSsoLoading } = useCheckSsoQuery(email);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  // Value will be -1 until PasswordStrengthBar is lazy-loaded
  const [passwordScore, setPasswordScore] = useState(-1);

  const [error, setError] = useState('');

  if (!email || !invitationToken) {
    return <Navigate to="/login" />;
  }

  const onSubmit = async () => {
    setError('');

    if (!name) {
      setError(
        t({
          id: 'join.error.missing-name',
          message: 'Name is required',
        })
      );

      return;
    }

    if (!isSso) {
      if (!password) {
        setError(
          t({
            id: 'forgotten-password.error.missing-password',
            message: 'Password is required',
          })
        );

        return;
      }

      if (password.length < MIN_PASSWORD_LENGTH) {
        setError(
          t({
            id: 'new-password.complexity.min-chars',
            message: `The password must be at least ${MIN_PASSWORD_LENGTH} characters long`,
          })
        );

        return;
      }

      if (passwordScore < 1) {
        setError(
          t({
            id: 'new-password.complexity.too-weak',
            message: 'The complexity of this password is too weak',
          })
        );

        return;
      }
    }

    await acceptInvitationAndLogin({
      email,
      invitationToken,
      name,
      password: isSso ? undefined : password,
    })
      .then(({ user, flags, publicKeys }) => {
        setCurrentSession(user);
        setFlags(flags);
        setPublicKeys(publicKeys);
      })
      .then(() => navigate('/requests', { replace: true }))
      .catch((err: any) => {
        if (err.message === 'Invalid email or invitation token') {
          setError(
            t({ id: 'join.error.email-or-token-invalid', message: 'Email or token invalid' })
          );
        } else {
          console.error(err);
          setError(
            (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
          );
        }
      });
  };

  if (isCheckSsoLoading) {
    return (
      <>
        <Title size="200" as="h1" align="center">
          <Trans id="join.title">Welcome to Prolong!</Trans>
        </Title>
        <Loader />
      </>
    );
  }

  return (
    <>
      <Title size="200" as="h1" align="center">
        <Trans id="join.title">Welcome to Prolong!</Trans>
      </Title>
      <Stack gap="1.5rem">
        <InputText
          isDisabled
          value={email}
          label={<Trans id="join.field.email.label">Email</Trans>}
        />
        <InputText
          isRequired
          name="name"
          label={<Trans id="join.field.name.label">Name</Trans>}
          placeholder={t({ id: 'join.field.name.placeholder', message: 'Enter your name...' })}
          autoComplete="name"
          value={name}
          onChange={(name) => {
            setName(name);
            setError('');
          }}
        />
        {!isSso && (
          <NewPassword
            password={password}
            setPassword={(password) => {
              setPassword(password);
              setError('');
            }}
            setStrengthScore={setPasswordScore}
          />
        )}
        {error && (
          <InlineText weight="medium" color="danger" align="center">
            {error}
          </InlineText>
        )}
        <Button
          type="submit"
          isLoading={isAcceptInvitationAndLoginPending || isAcceptInvitationAndLoginSuccess}
          disabled={!isSso && passwordScore === -1}
          onPress={onSubmit}
        >
          <Trans id="join.create-account">Create account</Trans>
        </Button>
      </Stack>
    </>
  );
};

export default Join;
